/**
 * CadenceBadge — ambient pill showing a peer's rotation
 * personality. Derived from their `/key-history` rows: median gap
 * between rotations + the current streak of consecutive
 * in-tolerance rotations anchored at the most recent.
 *
 * Rendered in two variants:
 *   compact  — "3d · 47 streak"  inline next to an avatar name.
 *   verbose  — "Rotates every 3d · 47-rotation streak" in the
 *              SafetyNumberDialog below the cadence sparkline.
 *
 * Privacy posture: cadence + streak are derivable by anyone who
 * fetches the peer's key-history anyway (same surface the avatar
 * ring already consumes). The badge adds no new data exposure.
 *
 * Gating rules:
 *   - Inferred cadence must be <= MAX_VISIBLE_CADENCE_DAYS (7).
 *     Peers with sloppier cadence silently render null so the
 *     badge stays aspirational — you only earn a badge if your
 *     rotation behaviour is actually tight.
 *   - Streak must be >= MIN_STREAK (3). A one-off rotation
 *     doesn't earn a pill.
 *   - Hidden in duress mode (the hook already returns null).
 */
import React from 'react';
import { Zap, TrendingUp, TrendingDown } from 'lucide-react';
import { useRotationHealthDetail } from '../hooks/useRotationHealth';

const MAX_VISIBLE_CADENCE_DAYS = 7;
const MIN_STREAK = 3;

// Human-readable trend copy. Kept in-module so the compact tooltip
// and verbose label share wording and never drift apart.
const TREND_LABEL = {
  up: 'trending up',
  down: 'trending down',
  flat: 'steady',
};

export const CadenceBadge = ({
  userId,
  variant = 'compact',
  className = '',
}) => {
  const detail = useRotationHealthDetail(userId);
  const cadence = detail?.cadence;
  if (!cadence) return null;
  if (!cadence.inferredCadenceDays) return null;
  if (cadence.inferredCadenceDays > MAX_VISIBLE_CADENCE_DAYS) return null;
  if ((cadence.streak || 0) < MIN_STREAK) return null;

  const cd = cadence.inferredCadenceDays;
  const st = cadence.streak;
  // Only surface 'up'/'down' — 'flat' and null stay invisible so
  // the badge doesn't add noise for peers whose cadence is simply
  // holding steady (which is the common case).
  const trend = detail?.trend === 'up' || detail?.trend === 'down' ? detail.trend : null;
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;

  if (variant === 'verbose') {
    return (
      <div
        className={`inline-flex items-center gap-1.5 text-xs text-primary/90 ${className}`}
        data-testid="cadence-badge-verbose"
        data-cadence={cd}
        data-streak={st}
        data-trend={trend || 'flat'}
        title={`Peer rotates every ~${cd}d; current streak of ${st} in-tolerance rotations${trend ? ` (${TREND_LABEL[trend]} vs 30d ago)` : ''}.`}
      >
        <Zap className="w-3.5 h-3.5" />
        <span>Rotates every {cd}d · {st}-rotation streak</span>
        {TrendIcon && (
          <span
            className={`inline-flex items-center gap-0.5 ${trend === 'up' ? 'text-emerald-300' : 'text-amber-300'}`}
            data-testid="cadence-badge-trend"
          >
            <TrendIcon className="w-3 h-3" />
            <span className="text-[10px]">{TREND_LABEL[trend]}</span>
          </span>
        )}
      </div>
    );
  }

  // Compact pill — sits inline next to a username without taking
  // more than ~5 characters of horizontal space. Emerald for
  // tighter-than-default cadence (≤3d), primary-blue otherwise.
  const tight = cd <= 3;
  const ring = tight
    ? 'border-emerald-400/35 bg-emerald-500/10 text-emerald-300'
    : 'border-primary/35 bg-primary/10 text-primary';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-[1px] text-[10px] font-mono leading-none ${ring} ${className}`}
      data-testid="cadence-badge-compact"
      data-cadence={cd}
      data-streak={st}
      data-trend={trend || 'flat'}
      title={`Rotates every ~${cd}d · ${st}-rotation streak${trend ? ` · ${TREND_LABEL[trend]}` : ''}`}
    >
      <Zap className="w-2.5 h-2.5" />
      {cd}d · {st}
      {TrendIcon && (
        <TrendIcon
          className={`w-2.5 h-2.5 ml-0.5 ${trend === 'up' ? 'text-emerald-300' : 'text-amber-300'}`}
          data-testid="cadence-badge-trend"
        />
      )}
    </span>
  );
};

export default CadenceBadge;
